import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarList, Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, SubTabs, TextInput,
  fmtDate, panelGet, useAdminGet,
} from '../kit';
import { AdminLink, adminHref } from '../adminNav';
import type { AdminTargetIn } from '../adminNav';
import { Badge, Check, Drawer, MultiSelect } from '../email/emailKit';
import { fmtN, monthLabel } from '../monthly';

/**
 * Admin → Content & TRL → Sectors & keywords (owner, 2026-10-06: "Sub-sector
 * and major keyword wise pivot table also in admin panel for cosmos tech — any
 * more detailed insights around this add as well").
 *
 * Everything reads the live catalogue through Backend/admin_sectors.py
 * (section "content", actions sector_*), which keeps one cleaned snapshot of
 * every technology in memory (rebuilt every 20 min, or on "Refresh data"):
 *
 *  - Pivot: any two of sector / sub-sector / major keyword / university /
 *    country / TRL / Horizon / value lever / month added, as a heat table.
 *    A cell, a row or a column total opens the technologies behind it.
 *  - Sectors: each sector's profile and its sub-sectors.
 *  - Major keywords: keywords on at least N technologies, with their spread.
 *  - Insights: specialisation, leaders, white space, cross-sector keywords,
 *    sub-sector pairs, maturity, months loaded, taxonomy health.
 *
 * In Sectors and Insights a sub-sector, keyword, sector or month name jumps to
 * the Pivot with that filter set. In Major keywords a keyword opens its list
 * of technologies (its "Pivot" button jumps instead).
 *
 * Deep links (admin/adminNav.tsx): /admin?group=content&tab=sectors plus
 * view=<pivot|sectors|major_keywords|insights>, rows, cols (or none), show
 * (count|row|col|total), top_rows, top_cols, count_mode (all|first), min_kw,
 * hide_generic (1|0) and the filters major, sub, keyword, university (code),
 * country, trl_band, horizon, lever, month — lists pipe-separated (major=A|B).
 * The last pivot setup is remembered in this browser.
 */

const SECTION = 'content';
const VIEWS = ['Pivot', 'Sectors', 'Major keywords', 'Insights'];
const snake = (t: string) => t.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
const VIEW_PARAM: Record<string, string> = {
  ...Object.fromEntries(VIEWS.map(v => [snake(v), v])),
  keywords: 'Major keywords', keyword: 'Major keywords', sector: 'Sectors', overview: 'Sectors', insight: 'Insights',
};

// The backend's list (admin_sectors.DIMENSIONS) — used until sector_meta answers.
const DIMS: Dim[] = [
  { key: 'major', label: 'Sector' }, { key: 'sub', label: 'Sub-sector' }, { key: 'keyword', label: 'Major keyword' },
  { key: 'university', label: 'University' }, { key: 'country', label: 'Country' },
  { key: 'trl_band', label: 'TRL band' }, { key: 'trl', label: 'TRL' }, { key: 'horizon', label: 'Horizon' },
  { key: 'lever', label: 'Value lever' }, { key: 'month', label: 'Added to Cosmos (month)' },
];
const DIM_KEYS = DIMS.map(d => d.key);
const TRL_BANDS = ['Early (TRL 1-3)', 'Mid (TRL 4-6)', 'Late (TRL 7-9)', 'Not disclosed'];
const BAND_SHORT = ['Early', 'Mid', 'Late', 'Not disclosed'];
const BAND_TONE = ['bg-primary-300', 'bg-primary-500', 'bg-primary-700', 'bg-grayscale-600'];
const HORIZONS = ['H1', 'H2', 'H3', 'No Horizon data'];
const LEVERS = ['Performance', 'Productivity & Scale', 'Risk & Resilience', 'Sustainability', 'Economics'];
const MIN_KW = ['10', '20', '50', '100'];

type Dim = { key: string; label: string };
type Meta = {
  dimensions: Dim[]; majors: string[]; trl_bands: string[]; levers: string[];
  subs: { key: string; n: number; major: string }[];
  universities: { key: string; label: string }[]; countries: string[]; months: string[];
  techs: number; built_at: string; build_s: number; ttl_min: number;
};

type Filters = {
  major: string[]; sub: string[]; keyword: string; university: string[]; country: string[];
  trl_band: string[]; horizon: string[]; lever: string[]; month: string[];
};
const LIST_KEYS = ['major', 'sub', 'university', 'country', 'trl_band', 'horizon', 'lever', 'month'] as const;
type ListKey = typeof LIST_KEYS[number];
const EMPTY_FILTERS: Filters = {
  major: [], sub: [], keyword: '', university: [], country: [], trl_band: [], horizon: [], lever: [], month: [],
};
const FILTER_LABEL: Record<string, string> = {
  major: 'Sector', sub: 'Sub-sector', keyword: 'Keyword', university: 'University', country: 'Country',
  trl_band: 'TRL band', trl: 'TRL', horizon: 'Horizon', lever: 'Value lever', month: 'Added to Cosmos',
};

type Opts = { count_mode: 'all' | 'first'; min_kw: string; hide_generic: boolean };
type Show = 'count' | 'row' | 'col' | 'total';
const SHOW_LABEL: Record<Show, string> = { count: 'Counts', row: '% of row', col: '% of column', total: '% of total' };
const SHOW_BY_LABEL: Record<string, Show> = Object.fromEntries(
  (Object.keys(SHOW_LABEL) as Show[]).map(k => [SHOW_LABEL[k], k]));

type PivotCfg = {
  rows: string; cols: string; top_rows: string; top_cols: string; show: Show; filters: Filters; opts: Opts;
};
const DEFAULT_CFG: PivotCfg = {
  rows: 'sub', cols: 'major', top_rows: '50', top_cols: '15', show: 'count',
  filters: EMPTY_FILTERS, opts: { count_mode: 'all', min_kw: '20', hide_generic: true },
};
const TOP_ROWS = ['10', '20', '30', '50', '100', '200'];
const TOP_COLS = ['5', '10', '15', '20', '30', '60'];

type PRow = { key: string; label: string; total: number; cells: Record<string, number> };
type PCol = { key: string; label: string; total: number | null };
type PivotData = {
  rows_dim: string; cols_dim: string; rows: PRow[]; cols: PCol[]; grand_total: number;
  rows_total_values: number; cols_total_values: number; more_rows: number; more_cols: number;
  multi_valued: boolean; note: string; took_ms: number; built_at: string;
};

/** What the technologies drawer lists: the sector_techs filters, and what the clicked cell counted. */
type DrawerSpec = { title: string; params: Record<string, string>; expected?: number; notes?: string[] };
/** A hand-over to the Pivot tab: these filters (on an empty set) and, optionally, rows / columns. */
type Jump = { filters?: Partial<Filters>; rows?: string; cols?: string };

type Ctx = {
  meta: Meta | null;
  /** Bumped by "Refresh data" so every view re-reads. */
  dataTick: number;
  openTechs: (s: DrawerSpec) => void;
  goPivot: (j: Jump) => void;
  uniLabel: (code: string) => string;
  uniCode: (label: string) => string;
};

// Where each kind of name jumps to, the same from every view.
const jumpSub = (s: string): Jump => ({ filters: { sub: [s] }, rows: 'keyword', cols: 'university' });
const jumpKeyword = (k: string): Jump => ({ filters: { keyword: k }, rows: 'sub', cols: 'major' });
const jumpSector = (m: string): Jump => ({ filters: { major: [m] }, rows: 'sub', cols: 'university' });

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const LS_KEY = 'utech_admin_sectors:pivot';

const strList = (v: any): string[] => (Array.isArray(v) ? v.map(String).filter(Boolean) : []);

const loadCfg = (): PivotCfg => {
  try {
    const v = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
    if (!v || typeof v !== 'object') return DEFAULT_CFG;
    const rows = DIM_KEYS.includes(v.rows) ? v.rows : DEFAULT_CFG.rows;
    let cols = v.cols === 'none' || DIM_KEYS.includes(v.cols) ? v.cols : DEFAULT_CFG.cols;
    if (cols === rows) cols = rows === 'major' ? 'sub' : 'major';
    const f = v.filters || {};
    const filters: Filters = { ...EMPTY_FILTERS, keyword: typeof f.keyword === 'string' ? f.keyword : '' };
    LIST_KEYS.forEach(k => { filters[k] = strList(f[k]); });
    const o = v.opts || {};
    return {
      rows, cols,
      top_rows: TOP_ROWS.includes(String(v.top_rows)) ? String(v.top_rows) : DEFAULT_CFG.top_rows,
      top_cols: TOP_COLS.includes(String(v.top_cols)) ? String(v.top_cols) : DEFAULT_CFG.top_cols,
      show: (Object.keys(SHOW_LABEL) as Show[]).includes(v.show) ? v.show : 'count',
      filters,
      opts: {
        count_mode: o.count_mode === 'first' ? 'first' : 'all',
        min_kw: MIN_KW.includes(String(o.min_kw)) ? String(o.min_kw) : '20',
        hide_generic: o.hide_generic !== false,
      },
    };
  } catch { return DEFAULT_CFG; }
};

/** The filter + option params every sector_* view accepts. */
const filterParams = (f: Filters, o?: Opts): Record<string, string> => {
  const p: Record<string, string> = {};
  LIST_KEYS.forEach(k => { if (f[k].length) p[k] = f[k].join('|'); });
  if (f.keyword.trim()) p.keyword = f.keyword.trim();
  if (o) {
    p.count_mode = o.count_mode;
    p.min_kw = o.min_kw;
    p.hide_generic = o.hide_generic ? '1' : '0';
  }
  return p;
};

const activeFilterCount = (f: Filters) =>
  LIST_KEYS.reduce((a, k) => a + (f[k].length ? 1 : 0), 0) + (f.keyword.trim() ? 1 : 0);

/** 0-100 → "12%" / "3.4%" / "<0.1%". */
const fmtPct = (v: number | null | undefined): string => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  if (v === 0) return '0%';
  if (v < 0.1) return '<0.1%';
  return v < 10 ? `${v.toFixed(1)}%` : `${Math.round(v)}%`;
};
/** A percentage the backend already rounded (0-100). */
const pc = (v: any) => (v === null || v === undefined || v === '' ? '—' : `${v}%`);
const times = (v: any) => (v === null || v === undefined || v === '' ? '—' : `${v}×`);

const nameOf = (dim: string, key: string, label: string) => (dim === 'month' ? monthLabel(key) : label);

const downloadCsv = (name: string, lines: (string | number | null | undefined)[][]) => {
  const esc = (s: any) => `"${String(s ?? '').replace(/"/g, '""')}"`;
  const csv = lines.map(l => l.map(esc).join(',')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
};

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------
/** A label over a control. A div, not a <label>: a label around several buttons clicks the first one. */
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col gap-1 text-xs text-grayscale-1100 min-w-0">
    <span>{label}</span>
    {children}
  </div>
);

/** A name that jumps to the Pivot (or does whatever onClick says). */
const NameLink: React.FC<{ onClick: () => void; title?: string; children: React.ReactNode; disabled?: boolean }> =
  ({ onClick, title, children, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled} title={title || 'Open in Pivot'}
    className={`text-left max-w-full truncate ${disabled ? 'text-grayscale-1100 cursor-default'
      : 'text-primary-700 underline underline-offset-2 decoration-primary-300 hover:decoration-primary-700'}`}>
    {children}
  </button>
);

/** The four TRL bands as one 100 % bar. */
const TrlBar: React.FC<{ trl: Record<string, number> | undefined; wide?: boolean }> = ({ trl, wide }) => {
  const total = TRL_BANDS.reduce((a, k) => a + (trl?.[k] || 0), 0);
  if (!trl || !total) return <span className="text-grayscale-1100">—</span>;
  const tip = TRL_BANDS.map((k, i) => `${BAND_SHORT[i]}: ${fmtN(trl[k] || 0)} (${fmtPct((100 * (trl[k] || 0)) / total)})`).join('\n');
  return (
    <div className={`flex h-3 rounded overflow-hidden bg-grayscale-200 ${wide ? 'w-full' : 'w-32'}`} title={tip}>
      {TRL_BANDS.map((k, i) => (trl[k] ? (
        <div key={k} className={BAND_TONE[i]} style={{ width: `${(100 * trl[k]) / total}%` }} />
      ) : null))}
    </div>
  );
};

const TrlLegend: React.FC = () => (
  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-grayscale-1100">
    {TRL_BANDS.map((k, i) => (
      <span key={k} className="inline-flex items-center gap-1">
        <span className={`inline-block w-2.5 h-2.5 rounded-sm ${BAND_TONE[i]}`} />{k}
      </span>
    ))}
  </div>
);

const Stat: React.FC<{ label: string; value: React.ReactNode; hint?: string }> = ({ label, value, hint }) => (
  <div className="rounded-lg border bg-white px-3 py-2 min-w-0">
    <div className="text-[10px] uppercase tracking-wide text-grayscale-1100">{label}</div>
    <div className="text-sm font-bold text-grayscale-1300 tabular-nums">{value}</div>
    {hint && <div className="text-[10px] text-grayscale-1100">{hint}</div>}
  </div>
);

const Explain: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-xs text-grayscale-1100 -mt-1">{children}</p>
);

const horizonTone = (h: string): 'blue' | 'green' | 'amber' | 'grey' =>
  h === 'H1' ? 'blue' : h === 'H2' ? 'green' : h === 'H3' ? 'amber' : 'grey';

// ---------------------------------------------------------------------------
// Technologies drawer — what sits behind a cell, a total, a keyword
// ---------------------------------------------------------------------------
const TechsDrawer: React.FC<{ spec: DrawerSpec; ctx: Ctx; onClose: () => void }> = ({ spec, ctx, onClose }) => {
  const key = JSON.stringify(spec.params);
  const [limit, setLimit] = useState(200);
  useEffect(() => { setLimit(200); }, [key]);
  const res = useAdminGet<any>(SECTION, 'sector_techs', { ...spec.params, limit }, [key, limit]);
  const total: number = res.data?.total ?? 0;
  const rows = useMemo(() => ((res.data?.rows || []) as any[]).map(r => ({
    id: r.id, title: r.title, university: r.university, university_code: r.university_code, country: r.country,
    trl: r.trl ? `TRL ${r.trl}` : 'Not disclosed', trl_band: r.trl_band, horizon: r.horizon,
    sectors: (r.sectors || []).join('; '), sub_sectors: (r.sub_sectors || []).join('; '),
    keywords: (r.keywords || []).join(', '), levers: (r.levers || []).join('; '),
    added: r.added ? String(r.added).slice(0, 10) : '',
  })), [res.data]);

  const scope = Object.entries(spec.params)
    .filter(([k, v]) => FILTER_LABEL[k] && v)
    .map(([k, v]) => `${FILTER_LABEL[k]}: ${v.split('|')
      .map(x => (k === 'university' ? ctx.uniLabel(x) : k === 'month' ? monthLabel(x) : x)).join(' or ')}`);
  if (spec.params.count_mode === 'first') scope.push('Counting the first sector only');

  const mismatch = res.data && spec.expected !== undefined && total !== spec.expected;
  return (
    <Drawer title={spec.title} onClose={onClose}
      subtitle={res.data
        ? `${fmtN(total)} technolog${total === 1 ? 'y' : 'ies'}${rows.length < total ? ` · showing the first ${fmtN(rows.length)}` : ''} · highest TRL first`
        : 'Loading…'}>
      {scope.length > 0 && (
        <div className="flex flex-wrap gap-1.5">{scope.map(s => <Badge key={s} tone="grey">{s}</Badge>)}</div>
      )}
      {(spec.notes || []).map(n => <div key={n} className="text-xs text-grayscale-1100">{n}</div>)}
      {mismatch && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          This list has {fmtN(total)}; the table showed {fmtN(spec.expected)}. Technologies tagged with several
          values (or a filter on the same thing you clicked) make the two sets slightly different.
        </div>
      )}
      {res.error && <Msg kind="error">{res.error}</Msg>}
      {res.loading && !res.data ? <Loading label="Listing technologies…" /> : (
        <DataTable rows={rows} csvName="sector_technologies" tableId="sectors_drawer_techs" maxHeight="60vh"
          columns={['title', 'university', 'trl', 'horizon', 'sectors', 'sub_sectors', 'keywords']}
          optionalColumns={['id', 'country', 'trl_band', 'levers', 'added']}
          labels={{ id: 'UTech ID', trl: 'TRL', trl_band: 'TRL band', sub_sectors: 'Sub-sectors', keywords: 'Top keywords',
            levers: 'Value levers', added: 'Added to Cosmos' }}
          empty="No technologies match."
          render={{
            title: r => (
              <AdminLink to={{ group: 'tech_pages', tab: 'tech', target: r.id }}
                title={`${r.title}\n${r.id} — open its Tech URL page`}>
                {r.title}
              </AdminLink>
            ),
            horizon: r => (
              <AdminLink to={{ group: 'horizon', tab: 'technologies', tech: r.id }} className="no-underline"
                title="Open in Horizon data">
                <Badge tone={horizonTone(r.horizon)}>{r.horizon === 'No Horizon data' ? 'none' : r.horizon}</Badge>
              </AdminLink>
            ),
          }}
          actions={r => (
            <span className="flex gap-2 text-xs">
              <AdminLink to={{ group: 'tech_pages', tab: 'tech', target: r.id }} title="Tech URL page">Page</AdminLink>
              <AdminLink to={{ group: 'horizon', tab: 'technologies', tech: r.id }} title="Horizon data">Horizon</AdminLink>
            </span>
          )} />
      )}
      {res.data && rows.length < total && limit < 2000 && (
        <div><Btn small onClick={() => setLimit(2000)}>Show up to 2,000</Btn></div>
      )}
    </Drawer>
  );
};

// ---------------------------------------------------------------------------
// Pivot
// ---------------------------------------------------------------------------
const PivotMatrix: React.FC<{
  d: PivotData; show: Show; rowsLabel: string; colsLabel: string;
  onCell: (r: PRow, c: PCol, n: number) => void; onRow: (r: PRow) => void;
  onCol: (c: PCol) => void; onAll: () => void;
}> = ({ d, show, rowsLabel, colsLabel, onCell, onRow, onCol, onAll }) => {
  const measure = useCallback((n: number, r: PRow, c: PCol): number | null => {
    if (show === 'count') return n;
    const of = show === 'row' ? r.total : show === 'col' ? c.total : d.grand_total;
    return of ? (100 * n) / of : null;
  }, [show, d.grand_total]);
  const fmt = (v: number | null) => (v === null ? '—' : show === 'count' ? fmtN(v) : fmtPct(v));
  // Shading follows the shown number; the folded "Other" column is left grey
  // so one big leftover bucket does not wash out the rest of the table.
  const max = useMemo(() => Math.max(0, ...d.rows.flatMap(r => d.cols
    .filter(c => c.key !== '__other__')
    .map(c => measure(r.cells[c.key] || 0, r, c) ?? 0))), [d, measure]);
  const alpha = (v: number) => (max ? 0.06 + 0.84 * (v / max) : 0);
  const rName = (r: PRow) => nameOf(d.rows_dim, r.key, r.label);
  const cName = (c: PCol) => (c.key === '__other__' || c.key === '__all__' ? c.label : nameOf(d.cols_dim, c.key, c.label));
  const tip = (r: PRow, c: PCol, n: number) => {
    const parts = [`${rName(r)} × ${cName(c)}: ${fmtN(n)} technolog${n === 1 ? 'y' : 'ies'}`];
    if (r.total) parts.push(`${fmtPct((100 * n) / r.total)} of the row`);
    if (c.total) parts.push(`${fmtPct((100 * n) / c.total)} of the column`);
    if (c.key === '__other__') parts.push('summed over the columns not shown');
    return parts.join(' · ') + (n ? '\nClick to list them' : '');
  };
  const LABEL_W = 'w-44 min-w-[11rem] max-w-[11rem] sm:w-64 sm:min-w-[16rem] sm:max-w-[16rem]';
  const TOTAL_W = 'w-[5.5rem] min-w-[5.5rem] max-w-[5.5rem] left-44 sm:left-64';

  return (
    <div className="overflow-auto max-h-[70vh] max-w-full rounded-lg border">
      <table className="text-xs border-separate" style={{ borderSpacing: 0 }}>
        <thead>
          <tr>
            <th className={`sticky top-0 left-0 z-30 bg-grayscale-200 ${LABEL_W} px-2 py-2 text-left align-bottom font-grotesk font-medium text-grayscale-1100`}>
              {rowsLabel} ↓ · {colsLabel} →
            </th>
            <th className={`sticky top-0 z-30 bg-grayscale-200 ${TOTAL_W} px-2 py-2 text-right align-bottom font-grotesk font-bold text-grayscale-1300 border-r`}>
              Total
            </th>
            {d.cols.map(c => (
              <th key={c.key} title={cName(c)}
                className="sticky top-0 z-20 bg-grayscale-200 w-24 min-w-[6rem] max-w-[6rem] px-1 py-2 align-bottom font-grotesk font-medium text-grayscale-1200">
                {c.key === '__other__' ? (
                  <span className="block text-center italic text-grayscale-1100 leading-tight [overflow-wrap:anywhere]"
                    title="The columns not shown, added together">{c.label}</span>
                ) : (
                  <button type="button" onClick={() => onCol(c)} title={`${cName(c)} — click to list this column's technologies`}
                    className="block w-full text-center leading-tight line-clamp-3 [overflow-wrap:anywhere] hover:text-primary-700">
                    {cName(c)}
                  </button>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {d.rows.map(r => (
            <tr key={r.key}>
              <th className={`sticky left-0 z-10 bg-white ${LABEL_W} px-2 py-0.5 text-left font-normal border-t border-grayscale-200`}>
                <button type="button" onClick={() => onRow(r)} title={`${rName(r)} — click to list this row's technologies`}
                  className="block w-[10rem] sm:w-[15rem] truncate text-left text-grayscale-1300 hover:text-primary-700">
                  {rName(r)}
                </button>
              </th>
              <td className={`sticky z-10 bg-grayscale-200 ${TOTAL_W} px-2 py-0.5 text-right tabular-nums font-bold text-grayscale-1300 border-t border-r border-grayscale-200`}>
                <button type="button" onClick={() => onRow(r)} className="hover:text-primary-700"
                  title={`${rName(r)}: ${fmtN(r.total)} technologies — click to list them`}>
                  {fmtN(r.total)}
                </button>
              </td>
              {d.cols.map(c => {
                const n = r.cells[c.key] || 0;
                const v = measure(n, r, c);
                const other = c.key === '__other__';
                const a = n && !other && v ? alpha(v) : 0;
                return (
                  <td key={c.key} className="p-[1px] bg-white border-t border-grayscale-200">
                    <button type="button" disabled={!n} onClick={() => onCell(r, c, n)} title={tip(r, c, n)}
                      className={`w-full h-7 rounded tabular-nums text-[11px] ${n ? 'hover:ring-2 hover:ring-primary-500' : 'text-grayscale-600 cursor-default'}
                        ${other ? 'italic' : ''}`}
                      style={{
                        background: n ? (other ? '#f5f5f5' : `rgba(128, 101, 63, ${a.toFixed(3)})`) : undefined,
                        color: n ? (a > 0.5 ? '#ffffff' : '#141414') : undefined,
                      }}>
                      {n ? fmt(v) : '·'}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th className={`sticky bottom-0 left-0 z-30 bg-grayscale-200 ${LABEL_W} px-2 py-1.5 text-left font-grotesk font-bold text-grayscale-1300`}>
              Total (technologies)
            </th>
            <td className={`sticky bottom-0 z-30 bg-grayscale-200 ${TOTAL_W} px-2 py-1.5 text-right tabular-nums font-bold text-grayscale-1300 border-r`}>
              <button type="button" onClick={onAll} className="hover:text-primary-700"
                title="Every technology matching the filters — click to list them">
                {fmtN(d.grand_total)}
              </button>
            </td>
            {d.cols.map(c => (
              <td key={c.key} className="sticky bottom-0 z-20 bg-grayscale-200 px-1 py-1.5 text-center tabular-nums font-bold text-grayscale-1200">
                {c.total === null || c.key === '__other__' ? '—' : (
                  <button type="button" onClick={() => onCol(c)} className="hover:text-primary-700"
                    title={`${cName(c)}: ${fmtN(c.total)} technologies — click to list them`}>
                    {fmtN(c.total)}
                  </button>
                )}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const PivotTab: React.FC<{
  ctx: Ctx; cfg: PivotCfg; setCfg: React.Dispatch<React.SetStateAction<PivotCfg>>;
  refresh: () => void; refreshing: boolean;
}> = ({ ctx, cfg, setCfg, refresh, refreshing }) => {
  const meta = ctx.meta;
  const dims = meta?.dimensions?.length ? meta.dimensions : DIMS;
  const dimLabel = (k: string) => (k === 'none' ? 'Nothing' : dims.find(x => x.key === k)?.label || k);

  const base = useMemo(() => filterParams(cfg.filters, cfg.opts), [cfg.filters, cfg.opts]);
  const params = useMemo(() => ({
    rows: cfg.rows, cols: cfg.cols, top_rows: cfg.top_rows, top_cols: cfg.top_cols, ...base,
  }), [cfg.rows, cfg.cols, cfg.top_rows, cfg.top_cols, base]);
  const pkey = JSON.stringify(params);
  const res = useAdminGet<PivotData>(SECTION, 'sector_pivot', params, [pkey, ctx.dataTick]);
  const d = res.data;

  // Keyword is typed: applied after a pause (or on Enter), not on every key.
  const [kw, setKw] = useState(cfg.filters.keyword);
  useEffect(() => { setKw(cfg.filters.keyword); }, [cfg.filters.keyword]);
  useEffect(() => {
    if (kw.trim() === cfg.filters.keyword.trim()) return;
    const t = setTimeout(() => setCfg(c => ({ ...c, filters: { ...c.filters, keyword: kw.trim() } })), 600);
    return () => clearTimeout(t);
  }, [kw, cfg.filters.keyword, setCfg]);

  const setFilter = (k: ListKey, v: string[]) => setCfg(c => ({ ...c, filters: { ...c.filters, [k]: v } }));
  const setOpt = (patch: Partial<Opts>) => setCfg(c => ({ ...c, opts: { ...c.opts, ...patch } }));
  const setRows = (v: string) => setCfg(c => ({ ...c, rows: v, cols: v === c.cols ? c.rows : c.cols }));
  const setCols = (v: string) => setCfg(c => ({
    ...c, cols: v,
    rows: v !== c.rows ? c.rows : c.cols !== 'none' ? c.cols : (v === 'sub' ? 'major' : 'sub'),
  }));
  const swap = () => setCfg(c => (c.cols === 'none' ? c : { ...c, rows: c.cols, cols: c.rows }));
  const nFilters = activeFilterCount(cfg.filters);

  // ---- drill-down
  const replaced = (dim: string) => {
    const f = (cfg.filters as any)[dim];
    return (Array.isArray(f) ? f.length : !!f)
      ? [`Your ${FILTER_LABEL[dim] || dim} filter is replaced by the value you clicked.`] : [];
  };
  const openCell = (r: PRow, c: PCol, n: number) => {
    if (!d) return;
    const p: Record<string, string> = { ...base, [d.rows_dim]: r.key };
    const notes = replaced(d.rows_dim);
    if (c.key === '__other__') {
      notes.push('"Other" folds the columns not shown together, so this lists every technology in the row.');
    } else if (c.key !== '__all__') {
      p[d.cols_dim] = c.key;
      notes.push(...replaced(d.cols_dim));
    }
    const cn = c.key === '__other__' || c.key === '__all__' ? c.label : nameOf(d.cols_dim, c.key, c.label);
    ctx.openTechs({ title: `${nameOf(d.rows_dim, r.key, r.label)} × ${cn}`, params: p,
      expected: c.key === '__other__' ? r.total : n, notes });
  };
  const openRow = (r: PRow) => {
    if (!d) return;
    ctx.openTechs({ title: nameOf(d.rows_dim, r.key, r.label), params: { ...base, [d.rows_dim]: r.key },
      expected: r.total, notes: replaced(d.rows_dim) });
  };
  const openCol = (c: PCol) => {
    if (!d || c.key === '__other__') return;
    const p: Record<string, string> = { ...base };
    if (c.key !== '__all__') p[d.cols_dim] = c.key;
    ctx.openTechs({ title: c.key === '__all__' ? 'All matching technologies' : nameOf(d.cols_dim, c.key, c.label),
      params: p, expected: c.total ?? undefined, notes: c.key === '__all__' ? [] : replaced(d.cols_dim) });
  };
  const openAll = () => ctx.openTechs({ title: 'All matching technologies', params: { ...base }, expected: d?.grand_total });

  // ---- export / share
  const exportCsv = () => {
    if (!d) return;
    const v = (n: number, r: PRow, c: PCol): string | number => {
      if (cfg.show === 'count') return n;
      const of = cfg.show === 'row' ? r.total : cfg.show === 'col' ? c.total : d.grand_total;
      return of ? Math.round((1000 * n) / of) / 10 : '';
    };
    const rl = dimLabel(d.rows_dim);
    const cl = d.cols_dim === 'none' ? '' : dimLabel(d.cols_dim);
    const colName = (c: PCol) => (c.key === '__other__' || c.key === '__all__' ? c.label : nameOf(d.cols_dim, c.key, c.label));
    const lines: (string | number | null)[][] = [
      [`${rl}${cl ? ` \\ ${cl}` : ''} (${SHOW_LABEL[cfg.show]})`, 'Total (technologies)', ...d.cols.map(colName)],
      ...d.rows.map(r => [nameOf(d.rows_dim, r.key, r.label), r.total, ...d.cols.map(c => v(r.cells[c.key] || 0, r, c))]),
      ['Total (technologies)', d.grand_total, ...d.cols.map(c => c.total)],
    ];
    downloadCsv(`sectors_pivot_${d.rows_dim}_by_${d.cols_dim}_${cfg.show}_${new Date().toISOString().slice(0, 10)}.csv`, lines);
  };
  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    const t: Record<string, string> = {
      group: 'content', tab: 'sectors', view: 'pivot', rows: cfg.rows, cols: cfg.cols, show: cfg.show,
      top_rows: cfg.top_rows, top_cols: cfg.top_cols, ...base,
    };
    const url = `${window.location.origin}${adminHref(t as any)}`;
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { window.prompt('Copy this link:', url); }
  };

  // ---- options for the pickers
  const rowOpts = dims.map(x => x.key);
  const colOpts = ['none', ...dims.map(x => x.key)];
  const dimLabels = { none: 'Nothing (one total column)', ...Object.fromEntries(dims.map(x => [x.key, x.label])) };
  const subOpts = useMemo(() => (meta?.subs || []).map(s => s.key), [meta]);
  const subLabels = useMemo(() => Object.fromEntries((meta?.subs || []).map(s => [s.key, `${s.key} · ${fmtN(s.n)}`])), [meta]);
  const uniOpts = useMemo(() => (meta?.universities || []).map(u => u.key), [meta]);
  const uniLabels = useMemo(() => Object.fromEntries((meta?.universities || []).map(u => [u.key, u.label])), [meta]);
  const monthLabels = useMemo(() => Object.fromEntries((meta?.months || []).map(m => [m, monthLabel(m)])), [meta]);

  return (
    <div className="flex flex-col gap-4 min-w-0">
      <Section title="Pivot table"
        right={
          <span className="flex items-center gap-2 flex-wrap">
            <Btn small onClick={copyLink} title="Copy a link that opens this exact table">{copied ? 'Copied' : 'Copy link'}</Btn>
            <Btn small onClick={exportCsv} disabled={!d} title="The table as shown (rows, columns, totals)">Download CSV</Btn>
          </span>
        }>
        <Explain>
          Pick what goes down the side and across the top. Each cell counts technologies; darker means more.
          Click a cell, a row or a total to see the technologies behind it.
        </Explain>

        <div className="flex gap-3 items-end flex-wrap">
          <Field label="Rows">
            <Select value={cfg.rows} onChange={setRows} options={rowOpts} labels={dimLabels} />
          </Field>
          <button type="button" onClick={swap} disabled={cfg.cols === 'none'} title="Swap rows and columns"
            className="h-9 px-2 rounded-md border text-sm text-grayscale-1200 hover:bg-grayscale-200 disabled:opacity-40">⇄</button>
          <Field label="Columns">
            <Select value={cfg.cols} onChange={setCols} options={colOpts} labels={dimLabels} />
          </Field>
          <Field label="Rows shown">
            <Select value={cfg.top_rows} onChange={v => setCfg(c => ({ ...c, top_rows: v }))} options={TOP_ROWS}
              labels={Object.fromEntries(TOP_ROWS.map(x => [x, `Top ${x}`]))} />
          </Field>
          <Field label="Columns shown">
            <Select value={cfg.top_cols} onChange={v => setCfg(c => ({ ...c, top_cols: v }))} options={TOP_COLS}
              labels={Object.fromEntries(TOP_COLS.map(x => [x, `Top ${x}`]))} />
          </Field>
          <Field label="Show as">
            <SubTabs tabs={Object.values(SHOW_LABEL)} active={SHOW_LABEL[cfg.show]}
              onChange={t => setCfg(c => ({ ...c, show: SHOW_BY_LABEL[t] || 'count' }))} />
          </Field>
        </div>

        <div className="flex gap-3 items-end flex-wrap border-t pt-3">
          <MultiSelect label="Sector" options={meta?.majors || []} values={cfg.filters.major}
            onChange={v => setFilter('major', v)} placeholder="Any sector" />
          <MultiSelect label="Sub-sector" options={subOpts} labels={subLabels} values={cfg.filters.sub}
            onChange={v => setFilter('sub', v)} placeholder="Any sub-sector" />
          <Field label="Keyword (exact)">
            <input value={kw} onChange={e => setKw(e.target.value)} placeholder="e.g. drug delivery"
              onKeyDown={e => { if (e.key === 'Enter') setCfg(c => ({ ...c, filters: { ...c.filters, keyword: kw.trim() } })); }}
              className={`h-9 px-3 rounded-md border-[0.5px] bg-white text-sm w-48 ${cfg.filters.keyword ? 'border-primary-500' : 'border-primary-300'}`} />
          </Field>
          <MultiSelect label="University" options={uniOpts} labels={uniLabels} values={cfg.filters.university}
            onChange={v => setFilter('university', v)} placeholder="Any university" />
          <MultiSelect label="Country" options={meta?.countries || []} values={cfg.filters.country}
            onChange={v => setFilter('country', v)} placeholder="Any country" />
          <MultiSelect label="TRL band" options={meta?.trl_bands || TRL_BANDS} values={cfg.filters.trl_band}
            onChange={v => setFilter('trl_band', v)} placeholder="Any TRL" />
          <MultiSelect label="Horizon" options={HORIZONS} values={cfg.filters.horizon}
            onChange={v => setFilter('horizon', v)} placeholder="Any horizon" />
          <MultiSelect label="Value lever" options={meta?.levers || LEVERS} values={cfg.filters.lever}
            onChange={v => setFilter('lever', v)} placeholder="Any lever" />
          <MultiSelect label="Added to Cosmos" options={meta?.months || []} labels={monthLabels} values={cfg.filters.month}
            onChange={v => setFilter('month', v)} placeholder="Any month" />
          {nFilters > 0 && (
            <Btn small onClick={() => { setKw(''); setCfg(c => ({ ...c, filters: EMPTY_FILTERS })); }}>
              Clear {nFilters} filter{nFilters === 1 ? '' : 's'}
            </Btn>
          )}
        </div>

        <div className="flex gap-4 items-end flex-wrap border-t pt-3">
          <Field label="A technology with several sectors counts">
            <Select value={cfg.opts.count_mode} onChange={v => setOpt({ count_mode: v === 'first' ? 'first' : 'all' })}
              options={['all', 'first']} labels={{ all: 'Under every sector', first: 'Under its first sector only' }} />
          </Field>
          <Field label="Major keyword = used on at least">
            <Select value={cfg.opts.min_kw} onChange={v => setOpt({ min_kw: v })} options={MIN_KW}
              labels={Object.fromEntries(MIN_KW.map(x => [x, `${x} technologies`]))} />
          </Field>
          <div className="pb-2">
            <Check checked={cfg.opts.hide_generic} onChange={v => setOpt({ hide_generic: v })}>
              Hide generic keywords <span className="text-xs text-grayscale-1100">(ones that just repeat a sector name, e.g. “healthcare”)</span>
            </Check>
          </div>
        </div>

        {res.error && <Msg kind="error">{res.error}</Msg>}
        {!d ? (res.loading ? <Loading label="Building the table…" /> : null) : (
          <div className={`flex flex-col gap-2 min-w-0 ${res.loading ? 'opacity-60' : ''}`}>
            {d.grand_total === 0 || !d.rows.length ? (
              <div className="text-sm text-grayscale-1100 py-6 text-center">No technologies match these filters.</div>
            ) : (
              <PivotMatrix d={d} show={cfg.show} rowsLabel={dimLabel(d.rows_dim)}
                colsLabel={d.cols_dim === 'none' ? 'Total' : dimLabel(d.cols_dim)}
                onCell={openCell} onRow={openRow} onCol={openCol} onAll={openAll} />
            )}
            <div className="text-xs text-grayscale-1300">
              <b>{fmtN(d.grand_total)}</b> technologies match.
              {' '}Showing {fmtN(d.rows.length)} of {fmtN(d.rows_total_values)} {dimLabel(d.rows_dim).toLowerCase()} rows
              {d.more_rows > 0 && <> (raise “Rows shown” to see more)</>}
              {d.cols_dim !== 'none' && (
                <>
                  {' '}· {fmtN(d.cols.filter(c => c.key !== '__other__').length)} of {fmtN(d.cols_total_values)} columns
                  {d.more_cols > 0 && <>; the other {fmtN(d.more_cols)} are folded into “Other”</>}
                </>
              )}.
            </div>
            {d.multi_valued && d.note && <div className="text-xs text-grayscale-1100">Note: {d.note}</div>}
            <div className="text-[11px] text-grayscale-1100 flex items-center gap-1 flex-wrap">
              <span>Computed in {fmtN(d.took_ms)} ms · data as of {fmtDate(d.built_at)}
                {meta?.ttl_min ? ` (refreshed every ${meta.ttl_min} min)` : ''} ·</span>
              <button type="button" onClick={refresh} disabled={refreshing}
                className="underline underline-offset-2 text-primary-700 disabled:opacity-50">
                {refreshing ? 'Refreshing…' : 'Refresh data'}
              </button>
            </div>
          </div>
        )}
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sectors — each sector's profile and its sub-sectors
// ---------------------------------------------------------------------------
const MIN_SUB = ['5', '20', '50'];

const subRow = (s: any) => ({
  sub_sector: s.key, official: s.official ? 'Official' : 'Other label', techs: s.techs, pct: s.pct,
  all_techs: s.all_techs ?? s.techs, official_sector: s.official_sector || '',
  share_in_sector: s.share_in_sector, universities: s.universities, countries: s.countries,
  trl_bands: TRL_BANDS.map((k, i) => `${BAND_SHORT[i]} ${s.trl?.[k] || 0}`).join(' · '), _trl: s.trl,
  early: s.trl?.[TRL_BANDS[0]] || 0, mid: s.trl?.[TRL_BANDS[1]] || 0, late: s.trl?.[TRL_BANDS[2]] || 0,
  not_disclosed: s.trl?.[TRL_BANDS[3]] || 0,
  disclosed_pct: s.disclosed_pct, avg_trl: s.avg_trl, h1: s.h1, h2: s.h2, h3: s.h3, h2_pct: s.h2_pct,
  added_90d: s.added_90d,
  top_university: (s.top_universities || [])[0] ? `${s.top_universities[0].label} (${s.top_universities[0].share}%)` : '',
  keywords: (s.keywords || []).map((k: any) => k.keyword).join(', '),
});

const SUB_LABELS: Record<string, string> = {
  sub_sector: 'Sub-sector', official: 'Label', techs: 'Techs in this sector', pct: '% of catalogue',
  all_techs: 'All techs in sub-sector', official_sector: 'Official sector',
  share_in_sector: '% of sub-sector in this sector', universities: 'Universities', countries: 'Countries',
  trl_bands: 'TRL mix', early: 'Early (TRL 1-3)', mid: 'Mid (TRL 4-6)', late: 'Late (TRL 7-9)',
  not_disclosed: 'TRL not disclosed', disclosed_pct: 'TRL disclosed', avg_trl: 'Avg TRL',
  h1: 'H1', h2: 'H2', h3: 'H3', h2_pct: 'H2 share', added_90d: 'Added last 90 days',
  top_university: 'Top university', keywords: 'Distinctive keywords',
};

const SectorCard: React.FC<{ m: any; open: boolean; toggle: () => void; ctx: Ctx; minSub: string }> =
  ({ m, open, toggle, ctx, minSub }) => {
  const subs = useMemo(() => (open ? (m.subs || []).map(subRow) : []), [m, open]);
  return (
    <div className="bg-white rounded-xl border p-4 flex flex-col gap-3 min-w-0">
      <div className="flex items-center gap-3 flex-wrap">
        <button type="button" onClick={toggle} aria-expanded={open}
          className="flex items-center gap-2 text-left flex-1 min-w-[14rem]">
          <span className="text-grayscale-1100 w-3">{open ? '▾' : '▸'}</span>
          <span className="text-sm font-bold text-grayscale-1300 font-grotesk">{m.key}</span>
          <span className="text-xs text-grayscale-1100">
            {fmtN(m.techs)} techs · {m.pct}% of catalogue · {fmtN(m.sub_count)} sub-sectors
            {m.avg_trl !== null && m.avg_trl !== undefined ? ` · avg TRL ${m.avg_trl}` : ''}
          </span>
        </button>
        <TrlBar trl={m.trl} />
        <Btn small onClick={() => ctx.goPivot(jumpSector(m.key))} title="Sub-sectors × universities in this sector">
          Open in Pivot
        </Btn>
      </div>
      {open && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-2">
            <Stat label="Technologies" value={fmtN(m.techs)} />
            <Stat label="% of catalogue" value={`${m.pct}%`} />
            <Stat label="Universities" value={fmtN(m.universities)} hint={`${fmtN(m.countries)} countries`} />
            <Stat label="TRL disclosed" value={pc(m.disclosed_pct)} />
            <Stat label="Avg TRL" value={m.avg_trl ?? '—'} hint="where disclosed" />
            <Stat label="H1 / H2" value={`${fmtN(m.h1)} / ${fmtN(m.h2)}`} hint={m.h3 ? `H3 ${fmtN(m.h3)}` : `H2 share ${pc(m.h2_pct)}`} />
            <Stat label="Added last 90 days" value={fmtN(m.added_90d)} />
            <Stat label="Sub-sectors" value={fmtN(m.sub_count)} hint={`with ≥ ${minSub} techs`} />
          </div>
          <div className="flex flex-col gap-1">
            <TrlBar trl={m.trl} wide />
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-grayscale-1100">
              {TRL_BANDS.map((k, i) => (
                <span key={k} className="inline-flex items-center gap-1">
                  <span className={`inline-block w-2.5 h-2.5 rounded-sm ${BAND_TONE[i]}`} />
                  {k} {fmtN(m.trl?.[k] || 0)}
                </span>
              ))}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <div className="text-xs text-grayscale-1100">Top universities (share of this sector)</div>
              <div className="flex flex-wrap gap-1.5">
                {(m.top_universities || []).map((u: any) => (
                  <button key={u.key} type="button"
                    onClick={() => ctx.goPivot({ filters: { major: [m.key], university: [u.key] }, rows: 'sub', cols: 'trl_band' })}
                    title="Open in Pivot: this university's sub-sectors in this sector, by TRL"
                    className="rounded-full border px-2.5 py-0.5 text-xs text-grayscale-1300 hover:bg-primary-100">
                    {u.label} <span className="text-grayscale-1100">{u.share}% · {fmtN(u.n)}</span>
                  </button>
                ))}
                {!(m.top_universities || []).length && <span className="text-xs text-grayscale-1100">—</span>}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <div className="text-xs text-grayscale-1100">Distinctive keywords (more common here than across Cosmos)</div>
              <div className="flex flex-wrap gap-1.5">
                {(m.keywords || []).map((k: any) => (
                  <button key={k.keyword} type="button" onClick={() => ctx.goPivot(jumpKeyword(k.keyword))}
                    title={`${fmtN(k.n)} techs here · ${k.lift}× as common as across Cosmos — open in Pivot`}
                    className="rounded-full bg-primary-100 border border-primary-300 px-2.5 py-0.5 text-xs text-grayscale-1300 hover:bg-primary-200">
                    {k.keyword} <span className="text-grayscale-1100">{k.lift}×</span>
                  </button>
                ))}
                {!(m.keywords || []).length && <span className="text-xs text-grayscale-1100">—</span>}
              </div>
            </div>
          </div>
          <DataTable rows={subs} tableId="sectors_sub_table" csvName={`sub_sectors_${snake(m.key)}`} maxHeight="26rem"
            columns={['sub_sector', 'official', 'techs', 'all_techs', 'share_in_sector', 'universities', 'trl_bands',
              'disclosed_pct', 'avg_trl', 'h1', 'h2', 'added_90d', 'top_university']}
            optionalColumns={['pct', 'official_sector', 'countries', 'early', 'mid', 'late', 'not_disclosed', 'h3',
              'h2_pct', 'keywords']}
            labels={SUB_LABELS} unsortable={['trl_bands']}
            empty={`No sub-sector here has ${minSub} or more technologies.`}
            render={{
              sub_sector: r => <NameLink onClick={() => ctx.goPivot(jumpSub(r.sub_sector))}
                title="Open in Pivot: keywords × universities in this sub-sector">{r.sub_sector}</NameLink>,
              official: r => <Badge tone={r.official === 'Official' ? 'green' : 'amber'}>{r.official}</Badge>,
              techs: r => fmtN(r.techs),
              all_techs: r => fmtN(r.all_techs),
              pct: r => pc(r.pct),
              share_in_sector: r => pc(r.share_in_sector),
              trl_bands: r => <TrlBar trl={r._trl} />,
              disclosed_pct: r => pc(r.disclosed_pct),
              h2_pct: r => pc(r.h2_pct),
            }} />
        </>
      )}
    </div>
  );
};

const SectorsTab: React.FC<{ ctx: Ctx }> = ({ ctx }) => {
  const [minSub, setMinSub] = useState('20');
  const res = useAdminGet<any>(SECTION, 'sector_overview', { min_sub: minSub }, [minSub, ctx.dataTick]);
  const d = res.data;
  const majors: any[] = d?.majors || [];
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const isOpen = (k: string, i: number) => open[k] ?? i === 0;
  const k = d?.kpis || {};
  return (
    <div className="flex flex-col gap-4 min-w-0">
      {res.error && <Msg kind="error">{res.error}</Msg>}
      {!d ? (res.loading ? <Loading label="Profiling sectors…" /> : null) : (
        <>
          <KPIRow>
            <KPI label="Technologies" value={fmtN(k.techs)} />
            <KPI label="Sectors" value={fmtN(k.sectors)} />
            <KPI label="Sub-sectors" value={fmtN(k.sub_sectors)} hint={`with ≥ ${minSub} techs`} />
            <KPI label="Universities" value={fmtN(k.universities)} />
            <KPI label="Countries" value={fmtN(k.countries)} />
            <KPI label="Major keywords" value={fmtN(k.major_keywords)} hint="used on 20+ technologies" />
          </KPIRow>
          <div className="flex items-center gap-3 flex-wrap">
            <Field label="Show sub-sectors with at least">
              <Select value={minSub} onChange={setMinSub} options={MIN_SUB}
                labels={Object.fromEntries(MIN_SUB.map(x => [x, `${x} technologies`]))} />
            </Field>
            <span className="flex gap-2 self-end pb-0.5">
              <Btn small onClick={() => setOpen(Object.fromEntries(majors.map(m => [m.key, true])))}>Expand all</Btn>
              <Btn small onClick={() => setOpen(Object.fromEntries(majors.map(m => [m.key, false])))}>Collapse all</Btn>
            </span>
            <div className="self-end pb-1"><TrlLegend /></div>
          </div>
          <div className="text-xs text-grayscale-1100">
            A technology can sit in several sectors, so the sector counts add up to more than {fmtN(k.techs)}.
            Each sub-sector sits under its official sector, or under the sector most of its technologies are in
            when that differs; the figures count the ones in this sector (“All techs” is its full size). “Other
            label” is a name the AI used that is not on the official list. Click a sub-sector or keyword to open it
            in the Pivot.
          </div>
          {majors.map((m, i) => (
            <SectorCard key={m.key} m={m} ctx={ctx} minSub={minSub} open={isOpen(m.key, i)}
              toggle={() => setOpen(o => ({ ...o, [m.key]: !isOpen(m.key, i) }))} />
          ))}
          <div className="text-[11px] text-grayscale-1100">Computed in {fmtN(d.took_ms)} ms · data as of {fmtDate(d.built_at)}</div>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Major keywords
// ---------------------------------------------------------------------------
const KW_SORTS = ['techs', 'lift', 'sectors', 'sub_sectors', 'universities'];
const KW_SORT_LABELS: Record<string, string> = {
  techs: 'Most technologies', lift: 'Most over-represented (with a sector filter)', sectors: 'Spread over most sectors',
  sub_sectors: 'Spread over most sub-sectors', universities: 'Most universities',
};

const KeywordsTab: React.FC<{ ctx: Ctx }> = ({ ctx }) => {
  const meta = ctx.meta;
  const [kq, setKq] = useState('');
  const [kqApplied, setKqApplied] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setKqApplied(kq.trim()), 450);
    return () => clearTimeout(t);
  }, [kq]);
  const [sort, setSort] = useState('techs');
  const [major, setMajor] = useState<string[]>([]);
  const [sub, setSub] = useState<string[]>([]);
  const [minKw, setMinKw] = useState('20');
  const [hideGeneric, setHideGeneric] = useState(true);
  const filtered = major.length > 0 || sub.length > 0;
  const filt: Filters = { ...EMPTY_FILTERS, major, sub };
  const opts: Opts = { count_mode: 'all', min_kw: minKw, hide_generic: hideGeneric };
  const params = { ...filterParams(filt, opts), kq: kqApplied, sort, limit: 300 };
  const res = useAdminGet<any>(SECTION, 'sector_keywords', params, [JSON.stringify(params), ctx.dataTick]);
  const d = res.data;
  const rows = useMemo(() => ((d?.rows || []) as any[]).map(r => ({
    ...r, main_sub_label: r.main_sub ? `${r.main_sub} (${r.main_sub_pct}%)` : '',
  })), [d]);
  const subOpts = useMemo(() => (meta?.subs || []).map(s => s.key), [meta]);
  const subLabels = useMemo(() => Object.fromEntries((meta?.subs || []).map(s => [s.key, `${s.key} · ${fmtN(s.n)}`])), [meta]);

  const openKeyword = (k: string) => ctx.openTechs({
    title: `Keyword: ${k}`, params: { ...filterParams({ ...filt, keyword: k }, opts) },
    expected: rows.find(r => r.keyword === k)?.techs,
  });

  const cols = ['keyword', 'techs', ...(filtered ? ['lift'] : []), 'sectors', 'sub_sectors', 'main_sub_label',
    'main_sector', 'universities', 'disclosed_pct', 'avg_trl', 'h2_pct', 'top_university'];
  return (
    <Section title="Major keywords"
      right={d ? <span className="text-xs text-grayscale-1100">{fmtN(d.total)} keywords · {fmtN(d.took_ms)} ms</span> : undefined}>
      <Explain>
        A major keyword is one used on at least {minKw} technologies. Click a keyword to list its technologies;
        “Pivot” shows which sub-sectors and sectors it appears in.
        {filtered && ' Lift: how much more common the keyword is in the chosen sector / sub-sector than across all of Cosmos (2× = twice as common).'}
      </Explain>
      <div className="flex gap-3 items-end flex-wrap">
        <Field label="Keyword contains">
          <TextInput value={kq} onChange={setKq} placeholder="e.g. battery" />
        </Field>
        <Field label="Sort by">
          <Select value={sort} onChange={setSort} options={KW_SORTS} labels={KW_SORT_LABELS} />
        </Field>
        <MultiSelect label="Sector" options={meta?.majors || []} values={major} onChange={setMajor} placeholder="All sectors" />
        <MultiSelect label="Sub-sector" options={subOpts} labels={subLabels} values={sub} onChange={setSub}
          placeholder="All sub-sectors" />
        <Field label="Major keyword = used on at least">
          <Select value={minKw} onChange={setMinKw} options={MIN_KW}
            labels={Object.fromEntries(MIN_KW.map(x => [x, `${x} technologies`]))} />
        </Field>
        <div className="pb-2">
          <Check checked={hideGeneric} onChange={setHideGeneric}>Hide generic keywords</Check>
        </div>
      </div>
      {sort === 'lift' && !filtered && (
        <div className="text-xs text-amber-800">Lift needs a sector or sub-sector filter — without one every keyword scores 1.</div>
      )}
      {res.error && <Msg kind="error">{res.error}</Msg>}
      {!d ? (res.loading ? <Loading label="Counting keywords…" /> : null) : (
        <div className={res.loading ? 'opacity-60' : ''}>
          <DataTable rows={rows} tableId="sectors_keywords" csvName="major_keywords" maxHeight="34rem"
            columns={cols} optionalColumns={['catalogue_techs', 'main_sub', 'main_sub_pct']}
            labels={{
              keyword: 'Keyword', techs: filtered ? 'Techs (in filter)' : 'Techs', catalogue_techs: 'Techs (all Cosmos)',
              lift: 'Lift', sectors: 'Sectors spanned', sub_sectors: 'Sub-sectors spanned',
              main_sub_label: 'Main sub-sector', main_sub: 'Main sub-sector (name)', main_sub_pct: 'Main sub-sector %',
              main_sector: 'Main sector', universities: 'Universities', disclosed_pct: 'TRL disclosed',
              avg_trl: 'Avg TRL', h2_pct: 'H2 share', top_university: 'Top university',
            }}
            empty="No keyword matches."
            render={{
              keyword: r => <NameLink onClick={() => openKeyword(r.keyword)} title="List its technologies">{r.keyword}</NameLink>,
              techs: r => fmtN(r.techs),
              lift: r => times(r.lift),
              disclosed_pct: r => pc(r.disclosed_pct),
              h2_pct: r => pc(r.h2_pct),
            }}
            actions={r => <Btn small onClick={() => ctx.goPivot(jumpKeyword(r.keyword))}
              title="Open in Pivot: sub-sectors × sectors for this keyword">Pivot</Btn>} />
          {d.total > rows.length && (
            <div className="text-[11px] text-grayscale-1100 mt-1">
              Showing the top {fmtN(rows.length)} of {fmtN(d.total)} — search or filter to narrow down.
            </div>
          )}
        </div>
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Insights
// ---------------------------------------------------------------------------
const InsightsTab: React.FC<{ ctx: Ctx }> = ({ ctx }) => {
  const res = useAdminGet<any>(SECTION, 'sector_insights', {}, [ctx.dataTick]);
  const d = res.data;
  const maturity = useMemo(() => ((d?.maturity || []) as any[]).map(m => {
    const trl = Object.fromEntries(TRL_BANDS.map(k => [k, m[k] || 0]));
    return {
      sector: m.sector, techs: m.techs, _trl: trl,
      trl_mix: TRL_BANDS.map((k, i) => `${BAND_SHORT[i]} ${trl[k]}`).join(' · '),
      early: trl[TRL_BANDS[0]], mid: trl[TRL_BANDS[1]], late: trl[TRL_BANDS[2]], not_disclosed: trl[TRL_BANDS[3]],
      disclosed_pct: m.disclosed_pct, avg_trl: m.avg_trl, late_pct_of_disclosed: m.late_pct_of_disclosed,
    };
  }), [d]);
  const months: any[] = d?.added_by_month || [];
  const monthByLabel = useMemo(() => Object.fromEntries(months.map(m => [monthLabel(m.month), m.month])), [months]);
  // Sector columns for the month table: the five biggest first, the rest on the Columns picker.
  const monthSectors = useMemo(() => {
    const keys = Array.from(new Set(months.flatMap(m => Object.keys(m).filter(k => k !== 'month' && k !== 'total'))));
    const sum = (k: string) => months.reduce((a, m) => a + (m[k] || 0), 0);
    return keys.sort((a, b) => sum(b) - sum(a));
  }, [months]);
  const h = d?.health || {};

  if (res.error) return <Msg kind="error">{res.error}</Msg>;
  if (!d) return res.loading ? <Loading label="Working out insights…" /> : null;
  return (
    <div className="flex flex-col gap-4 min-w-0">
      <div className="text-[11px] text-grayscale-1100">
        Across the whole catalogue (filters do not apply here) · computed in {fmtN(d.took_ms)} ms · data as of {fmtDate(d.built_at)}.
        Names in blue open the Pivot.
      </div>

      <Section title="University specialisation">
        <Explain>
          Specialisation = the university’s share of technologies in a sector ÷ the catalogue’s share in that sector.
          Above 1 means the university leans towards that sector more than Cosmos as a whole (2× = twice as focused).
          Universities with 50+ technologies and pairs with 20+.
        </Explain>
        <DataTable rows={d.specialisation || []} tableId="sectors_ins_spec" csvName="university_specialisation"
          columns={['university', 'sector', 'techs', 'share_of_university', 'share_of_catalogue', 'specialisation']}
          optionalColumns={['university_code']}
          labels={{ share_of_university: 'Share of the university', share_of_catalogue: 'Share of Cosmos', specialisation: 'Specialisation' }}
          render={{
            university: r => <NameLink onClick={() => ctx.goPivot({ filters: { university: [r.university_code], major: [r.sector] }, rows: 'sub', cols: 'trl_band' })}
              title="Open in Pivot: this university's sub-sectors in this sector, by TRL">{r.university}</NameLink>,
            sector: r => <NameLink onClick={() => ctx.goPivot(jumpSector(r.sector))}>{r.sector}</NameLink>,
            share_of_university: r => pc(r.share_of_university),
            share_of_catalogue: r => pc(r.share_of_catalogue),
            specialisation: r => <b>{times(r.specialisation)}</b>,
          }} />
      </Section>

      <Section title="Sector leaders">
        <Explain>The university with the most technologies in each sector, and its share of that sector.</Explain>
        <DataTable rows={d.leaders || []} tableId="sectors_ins_leaders" csvName="sector_leaders"
          columns={['sector', 'university', 'techs', 'share']} labels={{ share: 'Share of the sector' }}
          render={{
            sector: r => <NameLink onClick={() => ctx.goPivot(jumpSector(r.sector))}>{r.sector}</NameLink>,
            university: r => {
              const code = ctx.uniCode(r.university);
              return code ? (
                <NameLink onClick={() => ctx.goPivot({ filters: { university: [code], major: [r.sector] }, rows: 'sub', cols: 'trl_band' })}
                  title="Open in Pivot: this university's sub-sectors in this sector, by TRL">{r.university}</NameLink>
              ) : r.university;
            },
            share: r => pc(r.share),
          }} />
      </Section>

      <Section title="White space — thin sub-sectors">
        <Explain>
          Official sub-sectors with fewer than 40 technologies, or from 3 universities or fewer: thin coverage, and
          possible gaps to fill when loading new universities.
        </Explain>
        <DataTable rows={d.white_space || []} tableId="sectors_ins_white" csvName="white_space"
          columns={['sub_sector', 'sector', 'techs', 'universities', 'disclosed_pct']}
          labels={{ sub_sector: 'Sub-sector', disclosed_pct: 'TRL disclosed' }}
          render={{
            sub_sector: r => <NameLink disabled={!r.techs} onClick={() => ctx.goPivot(jumpSub(r.sub_sector))}
              title={r.techs ? 'Open in Pivot' : 'No technologies yet'}>{r.sub_sector}</NameLink>,
            disclosed_pct: r => pc(r.disclosed_pct),
          }} />
      </Section>

      <Section title="Cross-sector keywords">
        <Explain>Keywords (on 50+ technologies) that show up in the most sectors — the technologies that travel across industries.</Explain>
        <DataTable rows={((d.cross_sector_keywords || []) as any[]).map(r => ({ ...r, top_sectors: (r.top_sectors || []).join('; ') }))}
          tableId="sectors_ins_cross" csvName="cross_sector_keywords"
          columns={['keyword', 'techs', 'sectors', 'top_sectors']}
          labels={{ sectors: 'Sectors spanned', top_sectors: 'Biggest sectors' }}
          render={{ keyword: r => <NameLink onClick={() => ctx.goPivot(jumpKeyword(r.keyword))}>{r.keyword}</NameLink> }} />
      </Section>

      <Section title="Sub-sector pairs across sectors">
        <Explain>
          Sub-sectors from different sectors that are tagged on the same technologies more often than chance
          (lift 3× = three times as often as expected). 30+ shared technologies.
        </Explain>
        <DataTable rows={d.sub_pairs || []} tableId="sectors_ins_pairs" csvName="sub_sector_pairs"
          columns={['a', 'b', 'techs', 'lift']} labels={{ a: 'Sub-sector', b: 'Paired with', techs: 'Shared techs' }}
          render={{
            a: r => <NameLink onClick={() => ctx.goPivot(jumpSub(r.a))}>{r.a}</NameLink>,
            b: r => <NameLink onClick={() => ctx.goPivot(jumpSub(r.b))}>{r.b}</NameLink>,
            lift: r => <b>{times(r.lift)}</b>,
          }} />
      </Section>

      <Section title="Maturity by sector">
        <Explain>
          How far along each sector’s technologies are. Avg TRL counts only technologies whose TRL is disclosed;
          “Late share” is TRL 7-9 among those.
        </Explain>
        <TrlLegend />
        <DataTable rows={maturity} tableId="sectors_ins_maturity" csvName="maturity_by_sector"
          columns={['sector', 'techs', 'trl_mix', 'disclosed_pct', 'avg_trl', 'late_pct_of_disclosed']}
          optionalColumns={['early', 'mid', 'late', 'not_disclosed']} unsortable={['trl_mix']}
          labels={{ trl_mix: 'TRL mix', disclosed_pct: 'TRL disclosed', avg_trl: 'Avg TRL', late_pct_of_disclosed: 'Late share',
            early: 'Early (TRL 1-3)', mid: 'Mid (TRL 4-6)', late: 'Late (TRL 7-9)', not_disclosed: 'Not disclosed' }}
          render={{
            sector: r => <NameLink onClick={() => ctx.goPivot({ filters: { major: [r.sector] }, rows: 'sub', cols: 'trl_band' })}
              title="Open in Pivot: sub-sectors × TRL band">{r.sector}</NameLink>,
            techs: r => fmtN(r.techs),
            trl_mix: r => <TrlBar trl={r._trl} />,
            disclosed_pct: r => pc(r.disclosed_pct),
            late_pct_of_disclosed: r => pc(r.late_pct_of_disclosed),
          }} />
      </Section>

      <Section title="Added to Cosmos by month">
        <Explain>
          When technologies were loaded into Cosmos (a university at a time) — not when they were invented or
          published. A big month means a big load, not a market trend.
        </Explain>
        <BarList items={months.map(m => ({ label: monthLabel(m.month), value: m.total || 0 }))}
          onSelect={l => monthByLabel[l] && ctx.goPivot({ filters: { month: [monthByLabel[l]] }, rows: 'university', cols: 'major' })} />
        <div className="text-[11px] text-grayscale-1100">Click a month to see which universities and sectors were loaded.</div>
        <DataTable rows={months} tableId="sectors_ins_months" csvName="added_by_month" maxHeight="20rem"
          columns={['month', 'total', ...monthSectors.slice(0, 5)]} optionalColumns={monthSectors.slice(5)}
          labels={{ total: 'Technologies' }}
          render={{ month: r => monthLabel(r.month), total: r => fmtN(r.total) }} />
      </Section>

      <Section title="Taxonomy health">
        <Explain>How clean the sector tags are. The tags come from the AI, so the screen cleans them first; these are the leftovers.</Explain>
        <KPIRow>
          <KPI label="Technologies" value={fmtN(h.techs)} />
          <KPI label="In more than one sector" value={fmtN(h.multi_major)}
            hint={h.techs ? `${fmtPct((100 * (h.multi_major || 0)) / h.techs)} of all` : undefined} />
          <KPI label="No sub-sector" value={fmtN(h.no_sub)} />
          <KPI label="Raw sector labels" value={fmtN(h.raw_major_labels)} hint="cleaned into 14 sectors + Other" />
          <KPI label="Non-official sub-sector names" value={fmtN(h.noncanon_sub_values)} hint="kept as written" />
          <KPI label="Keyword plurals merged" value={fmtN(h.plural_folds)} hint="e.g. sensor / sensors" />
        </KPIRow>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1 min-w-0">
            <div className="text-xs text-grayscale-1100">
              Sector labels that are not one of the official names (folded into an official sector, or counted as Other)
            </div>
            <DataTable rows={((h.noncanon_majors || []) as any[]).map(([label, n]) => ({ label, techs: n }))}
              tableId="sectors_ins_raw_majors" csvName="nonofficial_sector_labels" maxHeight="20rem"
              labels={{ label: 'Label as tagged' }} empty="None — every sector label is official." />
          </div>
          <div className="flex flex-col gap-1 min-w-0">
            <div className="text-xs text-grayscale-1100">Sub-sector names not on the official list (most used first)</div>
            <DataTable rows={((h.noncanon_subs || []) as any[]).map(([label, n]) => ({ label, techs: n }))}
              tableId="sectors_ins_raw_subs" csvName="nonofficial_sub_sectors" maxHeight="20rem"
              labels={{ label: 'Sub-sector name' }} empty="None."
              render={{ label: r => <NameLink onClick={() => ctx.goPivot(jumpSub(r.label))}>{r.label}</NameLink> }} />
          </div>
        </div>
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// The screen
// ---------------------------------------------------------------------------
const SectorsKeywords: React.FC<{ target?: AdminTargetIn }> = ({ target }) => {
  const [view, setView] = useState(VIEWS[0]);
  // Views mount on first visit and then stay (hidden), keeping their state.
  const [seen, setSeen] = useState<Record<string, boolean>>({ [VIEWS[0]]: true });
  const pick = useCallback((v: string) => { setView(v); setSeen(s => (s[v] ? s : { ...s, [v]: true })); }, []);

  const [dataTick, setDataTick] = useState(0);
  const metaRes = useAdminGet<Meta>(SECTION, 'sector_meta', {}, [dataTick]);
  const meta = metaRes.data;

  const [cfg, setCfg] = useState<PivotCfg>(loadCfg);
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(cfg)); } catch { /* private mode */ }
  }, [cfg]);

  const [drawer, setDrawer] = useState<DrawerSpec | null>(null);
  useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawer(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawer]);

  const [refreshing, setRefreshing] = useState(false);
  const [refreshErr, setRefreshErr] = useState('');
  const refresh = useCallback(async () => {
    setRefreshing(true); setRefreshErr('');
    const { error } = await panelGet(SECTION, 'sector_meta', { refresh: '1' });
    setRefreshing(false);
    if (error) setRefreshErr(error);
    else setDataTick(t => t + 1);
  }, []);

  const goPivot = useCallback((j: Jump) => {
    setDrawer(null);
    setCfg(c => {
      let rows = j.rows && DIM_KEYS.includes(j.rows) ? j.rows : c.rows;
      let cols = j.cols && (j.cols === 'none' || DIM_KEYS.includes(j.cols)) ? j.cols : c.cols;
      if (rows === cols) cols = rows === 'major' ? 'sub' : 'major';
      return { ...c, rows, cols, filters: { ...EMPTY_FILTERS, ...(j.filters || {}) } };
    });
    pick('Pivot');
  }, [pick]);

  const uniMaps = useMemo(() => {
    const byCode: Record<string, string> = {}; const byLabel: Record<string, string> = {};
    (meta?.universities || []).forEach(u => { byCode[u.key] = u.label; byLabel[u.label] = u.key; });
    return { byCode, byLabel };
  }, [meta]);

  const ctx: Ctx = useMemo(() => ({
    meta: meta || null,
    dataTick,
    openTechs: (s: DrawerSpec) => setDrawer(s),
    goPivot,
    uniLabel: (code: string) => uniMaps.byCode[code] || code,
    uniCode: (label: string) => uniMaps.byLabel[label] || '',
  }), [meta, dataTick, goPivot, uniMaps]);

  // Deep link: a view, and/or a pivot setup (rows, cols, show, filters, options).
  useEffect(() => {
    if (!target) return;
    const p = (k: string) => String(target[k] ?? '').trim();
    const v = VIEWS.includes(p('view')) ? p('view') : VIEW_PARAM[snake(p('view'))];
    const listOf = (k: string) => p(k).split('|').map(x => x.trim()).filter(Boolean);
    const pivotKeys = ['rows', 'cols', 'show', 'top_rows', 'top_cols', 'count_mode', 'min_kw', 'hide_generic', 'keyword', ...LIST_KEYS];
    const givesPivot = pivotKeys.some(k => p(k));
    if (givesPivot) {
      setCfg(c => {
        let rows = DIM_KEYS.includes(p('rows')) ? p('rows') : c.rows;
        let cols = p('cols') === 'none' || DIM_KEYS.includes(p('cols')) ? p('cols') : c.cols;
        if (rows === cols) cols = rows === 'major' ? 'sub' : 'major';
        const anyFilter = p('keyword') || LIST_KEYS.some(k => p(k));
        const filters: Filters = anyFilter ? { ...EMPTY_FILTERS, keyword: p('keyword') } : c.filters;
        if (anyFilter) LIST_KEYS.forEach(k => { filters[k] = listOf(k); });
        const show = (Object.keys(SHOW_LABEL) as Show[]).includes(p('show') as Show) ? p('show') as Show : c.show;
        return {
          ...c, rows, cols, show, filters,
          top_rows: TOP_ROWS.includes(p('top_rows')) ? p('top_rows') : c.top_rows,
          top_cols: TOP_COLS.includes(p('top_cols')) ? p('top_cols') : c.top_cols,
          opts: {
            count_mode: p('count_mode') === 'first' ? 'first' : p('count_mode') === 'all' ? 'all' : c.opts.count_mode,
            min_kw: MIN_KW.includes(p('min_kw')) ? p('min_kw') : c.opts.min_kw,
            hide_generic: p('hide_generic') ? !['0', 'false', 'no'].includes(p('hide_generic').toLowerCase()) : c.opts.hide_generic,
          },
        };
      });
    }
    if (v) pick(v);
    else if (givesPivot) pick('Pivot');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.nonce]);

  const pane = (v: string, el: React.ReactNode) =>
    seen[v] ? <div key={v} className={view === v ? 'flex flex-col gap-4 min-w-0' : 'hidden'}>{el}</div> : null;

  return (
    <div className="flex flex-col gap-4 min-w-0">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <SubTabs tabs={VIEWS} active={view} onChange={pick} />
        <span className="text-[11px] text-grayscale-1100 flex items-center gap-1 flex-wrap">
          {meta && <span>{fmtN(meta.techs)} technologies · data as of {fmtDate(meta.built_at)} ·</span>}
          <button type="button" onClick={refresh} disabled={refreshing}
            title="Re-read the catalogue now (it is re-read every 20 minutes anyway)"
            className="underline underline-offset-2 text-primary-700 disabled:opacity-50">
            {refreshing ? 'Refreshing…' : 'Refresh data'}
          </button>
        </span>
      </div>
      {metaRes.error && <Msg kind="error">Filter lists could not load: {metaRes.error}</Msg>}
      {refreshErr && <Msg kind="error">Refresh failed: {refreshErr}</Msg>}
      {pane('Pivot', <PivotTab ctx={ctx} cfg={cfg} setCfg={setCfg} refresh={refresh} refreshing={refreshing} />)}
      {pane('Sectors', <SectorsTab ctx={ctx} />)}
      {pane('Major keywords', <KeywordsTab ctx={ctx} />)}
      {pane('Insights', <InsightsTab ctx={ctx} />)}
      {drawer && <TechsDrawer spec={drawer} ctx={ctx} onClose={() => setDrawer(null)} />}
    </div>
  );
};

export default SectorsKeywords;
